import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { Badge } from '../components/ui/badge'
import { Card, CardContent } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { useAuditLogsList } from '../hooks/useAuditLogs'
import { getApiError } from '../lib/api'
import type { AuditLog as AuditLogType } from '../types/auditLog'
import type { GetAuditLogsParams } from '../services/auditLogs'

const PAGE_SIZE = 50

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

export default function AuditLog() {
  const { t } = useTranslation()

  // Filters
  const [userIdInput, setUserIdInput] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all')
  const [actionFilter, setActionFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Pagination
  const [page, setPage] = useState(0)

  // Build query params
  const queryParams = useMemo((): GetAuditLogsParams => {
    const params: GetAuditLogsParams = {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }
    const parsedUserId = parseInt(userIdInput, 10)
    if (!isNaN(parsedUserId) && parsedUserId > 0) {
      params.userId = parsedUserId
    }
    if (entityTypeFilter !== 'all') {
      params.entityType = entityTypeFilter
    }
    if (actionFilter !== 'all') {
      params.action = actionFilter
    }
    if (dateFrom) {
      params.dateFrom = dateFrom
    }
    if (dateTo) {
      params.dateTo = dateTo
    }
    return params
  }, [userIdInput, entityTypeFilter, actionFilter, dateFrom, dateTo, page])

  const { data: logs = [], isLoading, isFetching, error, refetch } = useAuditLogsList(queryParams)

  const hasNextPage = logs.length === PAGE_SIZE
  const hasPrevPage = page > 0

  const handleEntityTypeChange = (value: string) => {
    setEntityTypeFilter(value)
    setPage(0)
  }

  const handleActionChange = (value: string) => {
    setActionFilter(value)
    setPage(0)
  }

  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    setPage(0)
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    setPage(0)
  }

  const handleUserIdChange = (value: string) => {
    setUserIdInput(value)
    setPage(0)
  }

  const renderChanges = (changes: Record<string, unknown> | null) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <span className="text-muted-foreground">-</span>
    }
    const jsonString = JSON.stringify(changes, null, 2)
    return (
      <span className="group relative inline-block cursor-pointer">
        <Badge variant="secondary" className="text-xs">
          {t('auditLog.changes')}
        </Badge>
        <span className="invisible group-hover:visible absolute z-50 left-0 top-full mt-1 w-80 max-h-60 overflow-auto rounded-md border bg-popover p-2 text-xs text-popover-foreground shadow-md whitespace-pre-wrap font-mono">
          {jsonString}
        </span>
      </span>
    )
  }

  // Error state
  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('auditLog.errorLoading')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('auditLog.title')}</h1>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" />
              {t('common.filters')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auditLog.filters.userId')}</label>
                <Input
                  type="number"
                  min={1}
                  placeholder={t('auditLog.filters.userIdPlaceholder')}
                  value={userIdInput}
                  onChange={(e) => handleUserIdChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auditLog.filters.entityType')}</label>
                <Select
                  value={entityTypeFilter}
                  onChange={(e) => handleEntityTypeChange(e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="invoice">{t('auditLog.entityTypes.invoice')}</option>
                  <option value="incoming_invoice">{t('auditLog.entityTypes.incomingInvoice')}</option>
                  <option value="client">{t('auditLog.entityTypes.client')}</option>
                  <option value="quarterly_report">{t('auditLog.entityTypes.quarterlyReport')}</option>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auditLog.filters.action')}</label>
                <Select
                  value={actionFilter}
                  onChange={(e) => handleActionChange(e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="create">{t('auditLog.actions.create')}</option>
                  <option value="update">{t('auditLog.actions.update')}</option>
                  <option value="annul">{t('auditLog.actions.annul')}</option>
                  <option value="correct">{t('auditLog.actions.correct')}</option>
                  <option value="status_change">{t('auditLog.actions.statusChange')}</option>
                  <option value="upload_file">{t('auditLog.actions.uploadFile')}</option>
                  <option value="delete_file">{t('auditLog.actions.deleteFile')}</option>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('common.from')}</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('common.to')}</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleDateToChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-36" />
                  <Skeleton className="h-10 w-16" />
                  <Skeleton className="h-10 w-20" />
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-10 w-28" />
                  <Skeleton className="h-10 w-16" />
                  <Skeleton className="h-10 w-20" />
                  <Skeleton className="h-10 w-28" />
                </div>
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold">{t('auditLog.noEntries')}</h3>
              <p className="text-muted-foreground">{t('auditLog.noEntriesDesc')}</p>
            </div>
          ) : (
            <>
              <div className="relative">
                {isFetching && !isLoading && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('auditLog.table.dateTime')}</TableHead>
                      <TableHead>{t('auditLog.table.userId')}</TableHead>
                      <TableHead>{t('auditLog.table.role')}</TableHead>
                      <TableHead>{t('auditLog.table.action')}</TableHead>
                      <TableHead>{t('auditLog.table.entityType')}</TableHead>
                      <TableHead>{t('auditLog.table.entityId')}</TableHead>
                      <TableHead>{t('auditLog.table.changes')}</TableHead>
                      <TableHead>{t('auditLog.table.ipAddress')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log: AuditLogType) => (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap">
                          {formatDateTime(log.createdAt)}
                        </TableCell>
                        <TableCell>{log.userId}</TableCell>
                        <TableCell>
                          <Badge variant={log.userRole === 'admin' ? 'default' : 'secondary'}>
                            {log.userRole}
                          </Badge>
                        </TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell>{log.entityType}</TableCell>
                        <TableCell>{log.entityId}</TableCell>
                        <TableCell>{renderChanges(log.changes)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {log.ipAddress ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {t('common.page')} {page + 1}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={!hasPrevPage || isFetching}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasNextPage || isFetching}
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
